import { DropdownContext } from 'components/DropdownContext';
import type { ProjectData, WorkOrderChangeData, WorkOrderChangeStatusEnum } from 'gql/graphql';
import { ContractStatusEnum, ProjectContractTypeEnum, ProjectMethodologyEnum, ProjectStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { ExportWorkOrderChangeBim } from 'pages/Project/BimProject/WorkOrderChange/ExportWorkOrderChangeBim/ExportWorkOrderChangeBim';
import { ExportWorkOrderChange } from 'pages/Project/Common/ExportWorkOrderChange';
import { ExportWorkOrderChangeAnalytics } from 'pages/Project/Common/ExportWorkOrderChangeAnalytics/ExportWorkOrderChangeAnalytics';
import { useGetWorkOrderChange } from 'pages/Project/UerProject/WorkOrderChange/hooks';
import { WorkOrderChangeCreateOrEdit } from 'pages/Project/UerProject/WorkOrderChange/WorkOrderChangeCreateOrEdit';
import { WorkOrderChangeRemove } from 'pages/Project/UerProject/WorkOrderChange/WorkOrderChangeRemove';
import { WorkOrderChangeStatus } from 'pages/Project/UerProject/WorkOrderChange/WorkOrderChangeStatus';
import { methodologyMap } from 'types/methodology';
import { Tooltip } from 'uikit';
import { getLabelWorkOrder } from 'utils/getLabelWorkOrder';

import { useCallback, useMemo, useState } from 'react';

import { TooltipNameWorkOrderChange, useAddItemsKebabTreeWorkOrderChange } from './useAddItemsKebabTreeWorkOrderChange';

export const KebabTreeWorkOrderChange = ({
    status,
    workOrderChangeId,
    refreshProject,
    projectData,
    statusContract,
    statusProject,
    methodology,
    workOrderChangeUerRules,
    workOrderId,
    label,
}: {
    status: WorkOrderChangeStatusEnum;
    workOrderChangeId: number;
    projectData: ProjectData | undefined | null;
    statusProject: ProjectStatusEnum | undefined;
    refreshProject: () => void;
    statusContract?: ContractStatusEnum;
    methodology: string | undefined;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    workOrderId: number;
    label: string;
}) => {
    const [isDeleteOpened, setIsDeleteOpened] = useState<boolean>(false);
    const [isEditOpened, setIsEditOpened] = useState<boolean>(false);
    const [isExportWorkOrderChangeAnalytics, setIsExportWorkOrderChangeAnalytics] = useState<boolean>(false);
    const [isExportWorkOrderChange, setIsExportWorkOrderChange] = useState<boolean>(false);

    const isBim = useMemo(() => methodology === methodologyMap[ProjectMethodologyEnum.Bim], [methodology]);

    const isFrameContract = projectData?.contractType === ProjectContractTypeEnum.Frame;

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = statusProject === ProjectStatusEnum.Completed;

    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const toggleDeleteHandler = useCallback(() => {
        setIsDeleteOpened((prev) => !prev);
    }, []);

    const toggleEditHandler = useCallback((value) => {
        setIsEditOpened(value);
    }, []);

    const onOpenExportWorkOrderChangeAnalyticsClick = useCallback(() => {
        setIsExportWorkOrderChangeAnalytics(true);
    }, []);

    const onCloseExportWorkOrderChangeAnalyticsClick = useCallback(() => {
        setIsExportWorkOrderChangeAnalytics(false);
    }, []);

    const toggleExportWorkOrderChange = useCallback(() => {
        setIsExportWorkOrderChange((prev) => !prev);
    }, []);

    const isDisabledByStatusProjectAndContract = isDisabledByProjectStatus || !isContractStatusWork;

    const {
        kebabItems,
        getMenuItemAttributes,
        tooltipPosition,
        activeTooltip,
        onMenuMouseMove,
        onMenuMouseLeave,
        BimDeleteOrderChangeErrorHandler,
        BimDeleteOrderChangeConfirmDialog,
    } = useAddItemsKebabTreeWorkOrderChange({
        statusWorkOrderChange: status,
        isDisabledByStatusProjectAndContract,
        methodology,
        toggleDeleteHandler,
        toggleEditHandler,
        toggleExportWorkOrderChange,
        workOrderChangeId,
        workOrderChangeUerRules,
        onOpenExportWorkOrderChangeAnalyticsClick,
        workOrderId,
    });

    const workOrderChangeIdForKebabEdit = useMemo(() => {
        if (isEditOpened) {
            return workOrderChangeId;
        }

        return null;
    }, [isEditOpened, workOrderChangeId]);

    const { workOrderChangeData, refreshWorkOrderChangeData } = useGetWorkOrderChange({
        workOrderChangeId: workOrderChangeIdForKebabEdit,
    });

    const { contractId, ...otherWorkOrderChangeData } = workOrderChangeData || ({} as WorkOrderChangeData);

    const workOrderLabel = useMemo(
        () =>
            getLabelWorkOrder({
                internalNumber: workOrderChangeData?.workOrderNumberText,
                signingDate: workOrderChangeData?.workOrderSigningDate,
            }),
        [workOrderChangeData?.workOrderNumberText, workOrderChangeData?.workOrderSigningDate],
    );

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <WorkOrderChangeStatus statusKey={status} isMinified size="s" style={{ marginRight: '8px' }} />
            <DropdownContext
                items={kebabItems}
                getItemAttributesHandler={getMenuItemAttributes}
                onMouseMoveHandler={onMenuMouseMove}
                onMouseLeaveHandler={onMenuMouseLeave}
            />
            {BimDeleteOrderChangeErrorHandler}
            {BimDeleteOrderChangeConfirmDialog}
            {isDeleteOpened && (
                <WorkOrderChangeRemove
                    refreshProject={refreshProject}
                    workOrderChangeId={workOrderChangeId}
                    isOpenedOuter={isDeleteOpened}
                    closeOuter={toggleDeleteHandler}
                />
            )}
            {isEditOpened && (
                <WorkOrderChangeCreateOrEdit
                    isOpenedOuter={isEditOpened}
                    contractId={contractId}
                    workOrderId={workOrderId}
                    projectData={projectData}
                    workOrderLabel={workOrderLabel}
                    refreshProjectData={refreshProject}
                    refreshWorkOrderChangeData={refreshWorkOrderChangeData}
                    workOrderChangeData={otherWorkOrderChangeData}
                    setIsOpenOuter={toggleEditHandler}
                    isDisabled={isDisabledByStatusProjectAndContract}
                    statusContract={statusContract}
                    statusProject={statusProject}
                />
            )}
            {isExportWorkOrderChangeAnalytics && (
                <ExportWorkOrderChangeAnalytics
                    workOrderChangeId={workOrderChangeId}
                    onClose={onCloseExportWorkOrderChangeAnalyticsClick}
                    label={label}
                />
            )}
            {isExportWorkOrderChange &&
                (isBim ? (
                    <ExportWorkOrderChangeBim
                        workOrderChangeId={workOrderChangeId}
                        onClose={toggleExportWorkOrderChange}
                    />
                ) : (
                    <ExportWorkOrderChange
                        workOrderChangeId={workOrderChangeId}
                        onClose={toggleExportWorkOrderChange}
                    />
                ))}
            {activeTooltip[TooltipNameWorkOrderChange.DeleteWorkOrderChange] && (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                    {isBim && !isFrameContract ? (
                        <Tooltip
                            size="s"
                            direction="upCenter"
                            position={tooltipPosition}
                            isInteractive={false}
                            style={{ zIndex: 2 }}
                        >
                            Удаление Изм. к Расчету по Договору возможно только в статусе
                            &quot;На&nbsp;формировании&quot;
                        </Tooltip>
                    ) : (
                        <Tooltip
                            size="s"
                            direction="upCenter"
                            position={tooltipPosition}
                            isInteractive={false}
                            style={{ zIndex: 2 }}
                        >
                            Удаление Изм. к НЗ возможно только в статусе &quot;На&nbsp;формировании&quot;
                        </Tooltip>
                    )}
                </>
            )}
        </div>
    );
};

KebabTreeWorkOrderChange.displayName = 'KebabTreeWorkOrderChange';
